import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DocPermissions from './DocPermissions';
import { DocIcon } from './DocIcons';
import { docsApi, errorOf, fullDate, LEVEL_LABEL } from '../../lib/docs';

/**
 * Settings -> Documentation, for administrators.
 *
 *   Overview         the numbers, and what readers found unhelpful
 *   Default access   the firm-wide rules every space inherits (seeded:
 *                    staff read, IT staff edit; contractors nothing)
 *   Groups           "Level 1 contractors", "Network specialists",
 *                    "FixIt Ltd's people": members by name and by rule
 *   Access report    pick a person: everything they can see, and why
 *   Archive          what was archived, to bring back
 *   Activity         every permission, group, publish and archive change
 *
 * Who may OPEN Documentation at all is Settings -> Features; this is what
 * they see inside it.
 */

const RULE_LABEL = { tier: 'Tier is', company: 'Company is', skill: 'Has the skill' };

function GroupEditor({ group, principals, onSaved, onCancel }) {
  const [name, setName] = useState(group?.name || '');
  const [description, setDescription] = useState(group?.description || '');
  const [members, setMembers] = useState(group?.members || []);
  const [rules, setRules] = useState(group?.rules || []);
  const [rule, setRule] = useState({ rule: 'tier', value: '' });
  const [pick, setPick] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const everyone = useMemo(() => [
    ...(principals?.contractors || []).map((c) => ({ kind: 'contractor', id: c.id, name: c.name, sub: [c.company, c.tier].filter(Boolean).join(' · ') })),
    ...(principals?.staff || []).map((s) => ({ kind: 'user', id: s.id, name: s.name, sub: 'Staff' }))
  ], [principals]);
  const values = rule.rule === 'tier' ? principals?.tiers || [] : rule.rule === 'company' ? principals?.companies || [] : principals?.skills || [];
  const save = async () => {
    setBusy(true); setErr(null);
    const body = { name, description, members: members.map((m) => ({ kind: m.kind, id: m.id })), rules };
    try {
      const r = group?.id ? await docsApi.patch(`/docs/groups/${group.id}`, body) : await docsApi.post('/docs/groups', body);
      onSaved(r.group);
    } catch (e) { setErr(errorOf(e)); }
    setBusy(false);
  };
  return (
    <div className="dsg-edit">
      <div className="dsg-row">
        <label className="dp-label" htmlFor="dsg-name">Name</label>
        <input id="dsg-name" className="de-input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="e.g. Level 1 contractors" />
      </div>
      <div className="dsg-row">
        <label className="dp-label" htmlFor="dsg-desc">Description</label>
        <input id="dsg-desc" className="de-input" value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} placeholder="Who this is, and why" />
      </div>
      <div className="dsg-cols">
        <div>
          <span className="dp-label">Members by rule</span>
          <p className="dp-hint">Checked every time - a contractor promoted to elite is in an “elite” group at once. Rules are for contractors only.</p>
          <ul className="dsg-chips">
            {rules.map((r, i) => (
              <li key={`${r.rule}-${r.value}`} className="dsg-chip">{RULE_LABEL[r.rule]} <strong>{r.value}</strong>
                <button type="button" aria-label={`Remove rule ${r.rule} ${r.value}`} onClick={() => setRules(rules.filter((_, j) => j !== i))}><DocIcon name="close" /></button></li>
            ))}
          </ul>
          <div className="dp-add">
            <select className="dp-select" aria-label="Rule" value={rule.rule} onChange={(e) => setRule({ rule: e.target.value, value: '' })}>
              <option value="tier">Tier is</option><option value="company">Company is</option><option value="skill">Has the skill</option>
            </select>
            <input className="dp-input dp-select-grow" list="dsg-values" aria-label="Value" value={rule.value} onChange={(e) => setRule({ ...rule, value: e.target.value })} placeholder="Value" />
            <datalist id="dsg-values">{values.map((v) => <option key={v} value={v} />)}</datalist>
            <button type="button" className="btn btn-secondary btn-small" disabled={!rule.value.trim()}
                    onClick={() => { setRules([...rules, { rule: rule.rule, value: rule.value.trim() }]); setRule({ ...rule, value: '' }); }}>Add rule</button>
          </div>
        </div>
        <div>
          <span className="dp-label">Members by name</span>
          <ul className="dsg-chips">
            {members.map((m) => (
              <li key={`${m.kind}-${m.id}`} className="dsg-chip">{m.name}
                <button type="button" aria-label={`Remove ${m.name}`} onClick={() => setMembers(members.filter((x) => !(x.kind === m.kind && x.id === m.id)))}><DocIcon name="close" /></button></li>
            ))}
          </ul>
          <div className="dp-add">
            <select className="dp-select dp-select-grow" aria-label="Add a person" value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">Choose a person…</option>
              {everyone.filter((p) => !members.some((m) => m.kind === p.kind && m.id === p.id)).map((p) => <option key={`${p.kind}:${p.id}`} value={`${p.kind}:${p.id}`}>{p.name} - {p.sub}</option>)}
            </select>
            <button type="button" className="btn btn-secondary btn-small" disabled={!pick}
                    onClick={() => { const [kind, id] = pick.split(':'); const p = everyone.find((x) => x.kind === kind && x.id === id); setMembers([...members, p]); setPick(''); }}>Add</button>
          </div>
        </div>
      </div>
      {err && <p className="dp-error" role="alert">{err}</p>}
      <div className="dp-foot">
        <button type="button" className="btn btn-primary" disabled={!name.trim() || busy} onClick={save}>{group?.id ? 'Save group' : 'Create group'}</button>
        <button type="button" className="btn btn-plain" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function Groups({ principals, reloadPrincipals }) {
  const [groups, setGroups] = useState(null);
  const [editing, setEditing] = useState(null);
  const [members, setMembers] = useState({});
  const [ask, setAsk] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(() => docsApi.get('/docs/groups').then((r) => setGroups(r.groups)).catch((e) => setErr(errorOf(e))), []);
  useEffect(() => { load(); }, [load]);
  const showMembers = async (g) => {
    if (members[g.id]) { setMembers({ ...members, [g.id]: null }); return; }
    try { const r = await docsApi.get(`/docs/groups/${g.id}/members`); setMembers({ ...members, [g.id]: r.members }); } catch (e) { setErr(errorOf(e)); }
  };
  const remove = async (g, force) => {
    setErr(null);
    try { await docsApi.del(`/docs/groups/${g.id}${force ? '?force=1' : ''}`); setAsk(null); load(); reloadPrincipals(); } catch (e) {
      if (e?.response?.status === 409) setAsk({ g, text: errorOf(e) }); else setErr(errorOf(e));
    }
  };
  return (
    <div className="dsg">
      <div className="dss-head">
        <p className="dp-lead">Groups let one rule cover many contractors - by tier, company or skill, or named one by one. Use them on any space, folder or article.</p>
        {!editing && <button type="button" className="btn btn-primary btn-small" onClick={() => setEditing({})}><DocIcon name="plus" />New group</button>}
      </div>
      {editing && <GroupEditor group={editing} principals={principals} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); reloadPrincipals(); }} />}
      {err && <p className="dp-error" role="alert">{err}</p>}
      {ask && (
        <div className="dp-ask" role="alert">
          <p>{ask.text}</p>
          <div className="dp-ask-actions">
            <button type="button" className="btn btn-danger btn-small" onClick={() => remove(ask.g, true)}>Delete it and its rules</button>
            <button type="button" className="btn btn-plain btn-small" onClick={() => setAsk(null)}>Keep it</button>
          </div>
        </div>
      )}
      {groups && !groups.length && !editing && <p className="dp-hint">No groups yet.</p>}
      <ul className="dsg-list">
        {(groups || []).map((g) => (
          <li key={g.id} className="dsg-card">
            <div className="dsg-card-head">
              <span className="dsg-name"><DocIcon name="users" />{g.name}</span>
              <span className="dsg-used">{g.used_in ? `used in ${g.used_in} rule${g.used_in === 1 ? '' : 's'}` : 'not used yet'}</span>
            </div>
            {g.description && <p className="dsg-desc">{g.description}</p>}
            <p className="dsg-sum">
              {g.rules.map((r) => `${RULE_LABEL[r.rule]} ${r.value}`).join(' or ') || 'No rules'}
              {g.members.length ? ` · plus ${g.members.map((m) => m.name).join(', ')}` : ''}
            </p>
            <div className="da-row da-wrap">
              <button type="button" className="btn btn-plain btn-small" onClick={() => showMembers(g)}>{members[g.id] ? 'Hide members' : 'Who is in it now?'}</button>
              <button type="button" className="btn btn-plain btn-small" onClick={() => setEditing(g)}>Edit</button>
              <button type="button" className="btn btn-plain btn-small" onClick={() => remove(g, false)}>Delete</button>
            </div>
            {members[g.id] && (
              <ul className="dsg-members">
                {members[g.id].map((m) => <li key={m.key}>{m.name}<small>{m.kind === 'contractor' ? 'Contractor' : 'Staff'} · {m.how}</small></li>)}
                {!members[g.id].length && <li className="dp-hint">Nobody, at the moment.</li>}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AccessReport({ principals }) {
  const [who, setWho] = useState('');
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!who) return undefined;
    let live = true;
    setRes(null); setErr(null);
    docsApi.get('/docs/admin/access', { person: who }).then((r) => { if (live) setRes(r); }).catch((e) => { if (live) setErr(errorOf(e)); });
    return () => { live = false; };
  }, [who]);
  return (
    <div className="dsr">
      <p className="dp-lead">Everything one person can open in Documentation, and the rule that lets them.</p>
      <select className="dp-select" aria-label="Person" value={who} onChange={(e) => setWho(e.target.value)}>
        <option value="">Choose a person…</option>
        <optgroup label="Contractors">{(principals?.contractors || []).map((c) => <option key={c.key} value={c.key}>{c.name}{c.company ? ` - ${c.company}` : ''}</option>)}</optgroup>
        <optgroup label="Staff">{(principals?.staff || []).map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}</optgroup>
      </select>
      {err && <p className="dp-error" role="alert">{err}</p>}
      {res && (
        <div className="dsr-res" aria-live="polite">
          <p className="dsr-sum"><strong>{res.person.name}</strong> can open {res.counts.articles} article{res.counts.articles === 1 ? '' : 's'} in {res.counts.spaces} space{res.counts.spaces === 1 ? '' : 's'}{res.counts.editable ? `, and edit ${res.counts.editable} item${res.counts.editable === 1 ? '' : 's'}` : ''}.
            {res.person.groups?.length > 0 && <> In: {res.person.groups.map((g) => g.name).join(', ')}.</>}</p>
          <div className="table-scroll">
          <table className="dsr-table">
            <thead><tr><th>Where</th><th>Access</th><th>Why</th></tr></thead>
            <tbody>
              {res.nodes.map((n) => (
                <tr key={n.id}>
                  <td><Link to={`/docs/${n.id}`}>{[...n.path, n.title].join(' › ')}</Link><small>{n.kind}</small></td>
                  <td><span className={`dp-level dp-level-${n.level}`}>{LEVEL_LABEL[n.level]}</span></td>
                  <td>{n.because}</td>
                </tr>
              ))}
              {!res.nodes.length && <tr><td colSpan={3}>Nothing at all.</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Activity() {
  const [rows, setRows] = useState(null);
  useEffect(() => { docsApi.get('/docs/admin/audit', { limit: 100 }).then((r) => setRows(r.entries)).catch(() => setRows([])); }, []);
  if (!rows) return <p className="dp-hint" aria-busy="true">Loading…</p>;
  return (
    <div>
      <p className="dp-lead">Permission and group changes, publishing and archiving. Also in <Link to="/audit-log">Audit Log</Link> as “Documentation”.</p>
      <ol className="dsa-list">
        {rows.map((r) => (
          <li key={r.id}><time dateTime={r.at}>{fullDate(r.at)}</time><strong>{r.actor_name || 'Somebody'}</strong>{r.on_behalf_by_name && <small> (by {r.on_behalf_by_name})</small>}<span>{r.said}</span></li>
        ))}
        {!rows.length && <li className="dp-hint">Nothing yet.</li>}
      </ol>
    </div>
  );
}

function Archive() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => docsApi.get('/docs/archived').then((r) => setRows(r.archived)).catch((e) => setErr(errorOf(e)));
  useEffect(() => { load(); }, []);
  const restore = async (id) => { try { await docsApi.post(`/docs/nodes/${id}/restore`, {}); load(); } catch (e) { setErr(errorOf(e)); } };
  return (
    <div>
      <p className="dp-lead">Nothing in Documentation is deleted. What was archived is here to bring back.</p>
      {err && <p className="dp-error" role="alert">{err}</p>}
      <ul className="dsa-list">
        {(rows || []).map((r) => (
          <li key={r.id}><span><strong>{r.title}</strong> <small>{r.kind}{r.path.length ? ` in ${r.path.join(' › ')}` : ''} · archived {fullDate(r.deleted_at)}{r.deleted_by_name ? ` by ${r.deleted_by_name}` : ''}</small></span>
            <button type="button" className="btn btn-secondary btn-small" onClick={() => restore(r.id)}>Bring back</button></li>
        ))}
        {rows && !rows.length && <li className="dp-hint">Nothing archived.</li>}
      </ul>
    </div>
  );
}

/*
 * The sub-tabs have addresses now (26 October): Settings -> Apps & content ->
 * Documentation -> /settings/content/documentation/<sub>, so a link, Back
 * and a bookmark all land on the same tab. `sub` is the address's word and
 * `onSub` changes it; without them (nothing passes neither any more, but a
 * component must not depend on that) the tab is kept here as it was.
 */
const SUB_OF = { overview: 'overview', default: 'default-access', groups: 'groups', report: 'access-report', archive: 'archive', activity: 'activity' };
const TAB_OF = Object.fromEntries(Object.entries(SUB_OF).map(([k, v]) => [v, k]));

export default function DocsSettings({ sub = null, onSub = null }) {
  const [ownTab, setOwnTab] = useState('overview');
  const tab = sub ? (TAB_OF[sub] || 'overview') : ownTab;
  const setTab = (id) => (typeof onSub === 'function' ? onSub(SUB_OF[id] || 'overview') : setOwnTab(id));
  const [overview, setOverview] = useState(null);
  const [principals, setPrincipals] = useState(null);
  const [notReady, setNotReady] = useState(null);
  const loadPrincipals = useCallback(() => docsApi.get('/docs/principals').then(setPrincipals).catch(() => {}), []);
  useEffect(() => {
    docsApi.get('/docs/admin/overview').then(setOverview).catch((e) => {
      if (e?.response?.status === 503) setNotReady(errorOf(e)); else setOverview({ failed: errorOf(e) });
    });
    loadPrincipals();
  }, [loadPrincipals]);
  if (notReady) {
    return (
      <div className="dss">
        <p className="dp-hint" role="status"><DocIcon name="info" />{notReady}</p>
      </div>
    );
  }
  const TABS = [['overview', 'Overview'], ['default', 'Default access'], ['groups', 'Groups'], ['report', 'Access report'], ['archive', 'Archive'], ['activity', 'Activity']];
  return (
    <div className="dss">
      <div className="dss-top">
        <div>
          <p className="dp-lead">What each person sees inside Documentation. Who may open it at all is the switch above (also on <strong>Feature access</strong>).</p>
        </div>
        <Link to="/docs" className="btn btn-secondary btn-small"><DocIcon name="book" />Open Documentation</Link>
      </div>
      {/* Settings draws these as links with addresses when it passes onSub. */}
      {typeof onSub !== 'function' && (
        <div className="dp-tabs" role="tablist" aria-label="Documentation settings">
          {TABS.map(([id, label]) => <button key={id} type="button" role="tab" aria-selected={tab === id} className={`dp-tab${tab === id ? ' is-on' : ''}`} onClick={() => setTab(id)}>{label}</button>)}
        </div>
      )}
      {tab === 'overview' && (
        !overview ? <p className="dp-hint" aria-busy="true">Loading…</p> : overview.failed ? <p className="dp-error">{overview.failed}</p> : (
          <div>
            <ul className="dss-stats">
              {[['Spaces', overview.spaces], ['Folders', overview.folders], ['Articles', overview.articles], ['Drafts', overview.drafts],
                ['Due for review', overview.reviews_due], ['Groups', overview.groups], ['Rules for contractors', overview.contractor_rules], ['Archived', overview.archived]].map(([l, n]) => (
                <li key={l}><strong>{n}</strong><span>{l}</span></li>
              ))}
            </ul>
            <div className="dhm-cols">
              <section className="dh-sec"><h3 className="dh-sec-title"><DocIcon name="eye" />Most read (30 days)</h3>
                <ul className="dsa-list">{overview.top.map((t) => <li key={t.id}><Link to={`/docs/${t.id}`}>{t.title}</Link><small>{t.views} views</small></li>)}{!overview.top.length && <li className="dp-hint">No reading yet.</li>}</ul></section>
              <section className="dh-sec"><h3 className="dh-sec-title"><DocIcon name="thumbdown" />Found unhelpful</h3>
                <ul className="dsa-list">{overview.unhelpful.map((t) => <li key={t.id}><Link to={`/docs/${t.id}`}>{t.title}</Link><small>{t.no} no · {t.yes} yes</small></li>)}{!overview.unhelpful.length && <li className="dp-hint">Nothing flagged.</li>}</ul></section>
            </div>
          </div>
        )
      )}
      {tab === 'default' && (
        <div>
          <p className="dp-lead">The rules every space starts from. A space, folder or article can add to them, override them, or stop inheriting them. Contractors see nothing until a rule gives it to them.</p>
          <DocPermissions nodeId="root" title="Firm-wide default" embedded />
        </div>
      )}
      {tab === 'groups' && <Groups principals={principals} reloadPrincipals={loadPrincipals} />}
      {tab === 'report' && <AccessReport principals={principals} />}
      {tab === 'archive' && <Archive />}
      {tab === 'activity' && <Activity />}
    </div>
  );
}
